import { useRef, useCallback } from "react";
import { CHART_TABS, ZONE_COLORS } from "./utils";

function formatHoverValue(mode, value) {
  if (!value) return null;
  if (mode === "hr") return `${Math.round(value)} bpm`;
  if (mode === "elevation") return `${Math.round(value)} m`;
  // pace: value is min/km as decimal
  const mins = Math.floor(value);
  const secs = Math.round((value - mins) * 60);
  return `${mins}:${secs.toString().padStart(2, "0")} /km`;
}

function formatYAxis(mode, value, isMax) {
  if (mode === "hr") return `${Math.round(value)} bpm`;
  if (mode === "elevation") return `${Math.round(value)} m`;
  // pace axis: max value = slowest pace (highest min/km), min value = fastest
  // displayed top = max (slowest), bottom = min (fastest) — but pace is inverted
  // chartMax = slowest (largest min/km number), chartMin = fastest
  const v = isMax ? value : value; // already correct direction
  const mins = Math.floor(v);
  const secs = Math.round((v - mins) * 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export default function RunChart({
  chartMode,
  hrData,
  velocityData,
  altitudeData,
  distanceData,
  timeData,
  availableTabs,
  hoverIndex,
  onHover,
  onHoverEnd,
  clipIdx,
  hasPostRun,
  hoveredZone,
  zoneConfig,
}) {
  const chartRef = useRef(null);

  const handleMouseMove = useCallback(
    e => {
      if (!chartRef.current) return;
      const rect = chartRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = Math.max(0, Math.min(1, x / rect.width));
      // Use longest available data array for index mapping
      const len = Math.max(hrData.length, velocityData.length, altitudeData.length);
      if (!len) return;
      onHover(Math.floor(pct * (len - 1)));
    },
    [hrData, velocityData, altitudeData, onHover]
  );

  // Build chart data for active mode
  let chartData = [];
  let chartColor = "#C0392B";
  if (chartMode === "hr" && hrData.length) {
    chartData = hrData;
    chartColor = "#C0392B";
  } else if (chartMode === "pace" && velocityData.length) {
    chartData = velocityData.map(v => (v > 0 ? 1000 / 60 / v : 0));
    chartColor = "#2E75B6";
  } else if (chartMode === "elevation" && altitudeData.length) {
    chartData = altitudeData;
    chartColor = "#27AE60";
  }

  const chartMin = chartData.length ? Math.min(...chartData.filter(Boolean)) * 0.97 : 0;
  const chartMax = chartData.length ? Math.max(...chartData.filter(Boolean)) * 1.03 : 100;
  const hoverValue = hoverIndex !== null ? chartData[hoverIndex] : null;

  // Actual data extremes (without padding) for min/max guide lines
  const actualMin = chartData.length ? Math.min(...chartData.filter(Boolean)) : null;
  const actualMax = chartData.length ? Math.max(...chartData.filter(Boolean)) : null;
  const yForVal = v => 100 - ((v - chartMin) / (chartMax - chartMin)) * 90 - 5;
  const yMinLine = actualMin !== null ? yForVal(actualMin) : null;
  const yMaxLine = actualMax !== null ? yForVal(actualMax) : null;

  // Build path for any data array, normalized to the same 0-100 viewBox
  function buildPath(data) {
    if (!data.length) return "";
    const dMin = Math.min(...data.filter(Boolean)) * 0.97;
    const dMax = Math.max(...data.filter(Boolean)) * 1.03;
    const step = 100 / (data.length - 1);
    const pts = data.map((v, i) => `${i * step},${100 - ((v - dMin) / (dMax - dMin)) * 90 - 5}`);
    return `M ${pts[0]} ${pts
      .slice(1)
      .map(p => `L ${p}`)
      .join(" ")}`;
  }

  const hoverTime =
    hoverIndex !== null && timeData[hoverIndex] != null
      ? (() => {
          const t = timeData[hoverIndex];
          const m = Math.floor(t / 60);
          const s = t % 60;
          return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
        })()
      : null;

  // Dot position on active chart line
  const dotPos =
    hoverIndex !== null && hoverValue
      ? {
          x: (hoverIndex / (chartData.length - 1)) * 100,
          y: 100 - ((hoverValue - chartMin) / (chartMax - chartMin)) * 90 - 5,
        }
      : null;

  return (
    <div
      style={{
        background: "var(--bg-card)",
        borderRadius: 14,
        padding: "16px",
        paddingTop: "20px",
        marginBottom: 12,
        overflow: "visible",
      }}
    >
      {/* Header row: label + hover value */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: "bold", color: "#1A1A2E" }}>
          {CHART_TABS.find(t => t.key === chartMode)?.label}
        </div>
        {hoverValue && (
          <span style={{ fontSize: 13, fontWeight: "bold", color: chartColor }}>
            {formatHoverValue(chartMode, hoverValue)}
          </span>
        )}
      </div>

      {/* Chart area */}
      <div
        ref={chartRef}
        style={{ position: "relative", height: 100, cursor: "crosshair", overflow: "visible" }}
        onMouseMove={handleMouseMove}
        onMouseLeave={onHoverEnd}
        onTouchMove={e => {
          e.preventDefault();
          handleMouseMove({ clientX: e.touches[0].clientX });
        }}
      >
        <svg
          width="100%"
          height="100"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{ overflow: "visible" }}
        >
          {/* Zone band highlight — rendered when hovering a zone in ZoneDistribution */}
          {chartMode === "hr" && hoveredZone && zoneConfig && (() => {
            const bounds = {
              1: [0, zoneConfig.z1_max],
              2: [zoneConfig.z1_max + 1, zoneConfig.z2_max],
              3: [zoneConfig.z2_max + 1, zoneConfig.z3_max],
              4: [zoneConfig.z3_max + 1, zoneConfig.z4_max],
              5: [zoneConfig.z4_max + 1, 999],
            };
            const [lo, hi] = bounds[hoveredZone];
            if (hi < chartMin || lo > chartMax) return null;
            const yTop = yForVal(Math.min(hi, chartMax));
            const yBot = yForVal(Math.max(lo, chartMin));
            const h = yBot - yTop;
            if (h <= 0) return null;
            return (
              <rect
                key="zone-band"
                x="0" y={yTop} width="100" height={h}
                fill={ZONE_COLORS[hoveredZone]}
                opacity="0.2"
              />
            );
          })()}

          {/* All lines — dimmed non-active, bright active */}
          {availableTabs.map(tab => {
            const isActive = tab.key === chartMode;
            let data = [];
            if (tab.key === "hr") data = hrData;
            else if (tab.key === "pace") data = velocityData.map(v => (v > 0 ? 1000 / 60 / v : 0));
            else if (tab.key === "elevation") data = altitudeData;
            if (!data.length) return null;
            return (
              <path
                key={tab.key}
                d={buildPath(data)}
                fill="none"
                stroke={isActive ? tab.color : "#D0D0D0"}
                strokeWidth={isActive ? "2.5" : "1"}
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                opacity={isActive ? 1 : 0.5}
              />
            );
          })}

          {/* Min/max horizontal guide lines */}
          {yMaxLine !== null && (
            <line
              x1="0" y1={yMaxLine} x2="100" y2={yMaxLine}
              stroke={chartColor} strokeWidth="0.8" strokeDasharray="3,3"
              vectorEffect="non-scaling-stroke" opacity="0.4"
            />
          )}
          {yMinLine !== null && (
            <line
              x1="0" y1={yMinLine} x2="100" y2={yMinLine}
              stroke={chartColor} strokeWidth="0.8" strokeDasharray="3,3"
              vectorEffect="non-scaling-stroke" opacity="0.4"
            />
          )}

          {/* Vertical hover line */}
          {hoverIndex !== null && dotPos && (
            <line
              x1={dotPos.x}
              y1="0"
              x2={dotPos.x}
              y2="100"
              stroke="rgba(0,0,0,0.2)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>

        {/* Min/max value labels */}
        {yMaxLine !== null && (
          <div
            style={{
              position: "absolute", left: 2, top: yMaxLine,
              transform: "translateY(-50%)", fontSize: 9,
              color: chartColor, opacity: 0.6,
              fontFamily: "monospace", pointerEvents: "none",
              background: "var(--bg-card)", padding: "0 2px", borderRadius: 2,
            }}
          >
            {formatYAxis(chartMode, actualMax, true)}
          </div>
        )}
        {yMinLine !== null && (
          <div
            style={{
              position: "absolute", left: 2, top: yMinLine,
              transform: "translateY(-50%)", fontSize: 9,
              color: chartColor, opacity: 0.6,
              fontFamily: "monospace", pointerEvents: "none",
              background: "var(--bg-card)", padding: "0 2px", borderRadius: 2,
            }}
          >
            {formatYAxis(chartMode, actualMin, false)}
          </div>
        )}

        {/* Floating time label above the cursor line — NRC style */}
        {dotPos && hoverTime && (
          <div
            style={{
              position: "absolute",
              left: `${dotPos.x}%`,
              top: -4,
              transform: "translateX(-50%)",
              background: "#1A1A2E",
              color: "#fff",
              fontSize: 11,
              fontFamily: "monospace",
              fontWeight: "bold",
              padding: "2px 6px",
              borderRadius: 5,
              pointerEvents: "none",
              whiteSpace: "nowrap",
            }}
          >
            {hoverTime}
          </div>
        )}

        {/* Post-run walk shading — light grey region from clipIdx to end */}
        {hasPostRun && clipIdx !== null && chartData.length > clipIdx && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: `${(clipIdx / (chartData.length - 1)) * 100}%`,
              width: `${((chartData.length - 1 - clipIdx) / (chartData.length - 1)) * 100}%`,
              height: "100%",
              background: "rgba(180,180,180,0.18)",
              borderLeft: "1.5px dashed rgba(150,150,150,0.5)",
              pointerEvents: "none",
              zIndex: 1,
            }}
          >
            <span
              style={{
                position: "absolute",
                top: 4,
                left: 5,
                fontSize: 9,
                color: "#aaa",
                fontFamily: "monospace",
                letterSpacing: 0.5,
                whiteSpace: "nowrap",
              }}
            >
              walk
            </span>
          </div>
        )}

        {/* Hover dot — as div so it's a perfect circle regardless of SVG scaling */}
        {dotPos && (
          <div
            style={{
              position: "absolute",
              left: `${dotPos.x}%`,
              top: `${dotPos.y}%`,
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: chartColor,
              border: "2px solid #fff",
              boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
            }}
          />
        )}

        {/* Y axis min/max */}
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            fontSize: 10,
            color: "#bbb",
            fontFamily: "monospace",
          }}
        >
          {formatYAxis(chartMode, chartMode === "pace" ? chartMin : chartMax, true)}
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 0,
            right: 0,
            fontSize: 10,
            color: "#bbb",
            fontFamily: "monospace",
          }}
        >
          {formatYAxis(chartMode, chartMode === "pace" ? chartMax : chartMin, false)}
        </div>
      </div>

      {/* X axis — distance markers */}
      {distanceData.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
          {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
            const idx = Math.floor(pct * (distanceData.length - 1));
            return (
              <span key={i} style={{ fontSize: 10, color: "#bbb", fontFamily: "monospace" }}>
                {(distanceData[idx] / 1000).toFixed(1)}km
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
